import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const navigate = useNavigate();

  const services = [
    { id: 'direct-taxation', name: 'Direct Taxation' },
    { id: 'indirect-taxation-gst', name: 'Indirect Taxation & GST' },
    { id: 'audit-assurance', name: 'Audit & Assurance' },
    { id: 'business-setup', name: 'Business Setup Services' },
    { id: 'corporate-law', name: 'Corporate Law Services' },
    { id: 'accounting-bookkeeping', name: 'Accounting & Bookkeeping' },
    { id: 'nri-services', name: 'NRI Services' },
    { id: 'business-advisory', name: 'Business Advisory' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  };

  const handleServiceClick = (serviceId) => {
    navigate(`/services/${serviceId}`);
    setIsServicesOpen(false);
    setIsMobileMenuOpen(false);
    setIsMobileServicesOpen(false);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center text-sm gap-2">
          <div className="flex items-center gap-4">
            <a href="tel:9636557944" className="flex items-center gap-2 hover:text-teal-400 transition-colors">
              <Phone className="w-4 h-4" />
              <span>+91 9636557944</span>
            </a>
            <a href="mailto:pankaj@pankajrgoyal.com" className="flex items-center gap-2 hover:text-teal-400 transition-colors">
              <Mail className="w-4 h-4" />
              <span>pankaj@pankajrgoyal.com</span>
            </a>
          </div>
          <div className="text-slate-300">
            Professional Chartered Accountant Services
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-lg' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <h1 className="text-2xl font-bold text-slate-900">
                <span className="text-teal-600">Pankaj R Goyal & Associates</span>
                <span className="block text-sm font-normal text-slate-600">Chartered Accountant</span>
              </h1>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection('home')}
                className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
              >
                About
              </button>
              
              {/* Services Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setIsServicesOpen(true)}
                onMouseLeave={() => setIsServicesOpen(false)}
              >
                <button
                  className="text-slate-700 hover:text-teal-600 font-medium transition-colors flex items-center gap-1"
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isServicesOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-slate-100 py-2 z-50">
                    {services.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => handleServiceClick(service.id)}
                        className="w-full text-left px-4 py-2 text-slate-700 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                      >
                        {service.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => scrollToSection('client-stories')}
                className="text-slate-700 hover:text-teal-600 font-medium transition-colors"
              >
                Client Stories
              </button>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                Contact Us
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-slate-700"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200">
            <nav className="flex flex-col px-4 py-4 space-y-1">
              <button
                onClick={() => scrollToSection('home')}
                className="text-slate-700 hover:text-teal-600 font-medium text-left py-2 transition-colors"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-slate-700 hover:text-teal-600 font-medium text-left py-2 transition-colors"
              >
                About
              </button>
              
              {/* Mobile Services Dropdown */}
              <div>
                <button
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                  className="w-full text-slate-700 hover:text-teal-600 font-medium text-left py-2 transition-colors flex items-center justify-between"
                >
                  Services
                  <ChevronDown className={`w-4 h-4 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileServicesOpen && (
                  <div className="pl-4 space-y-1 border-l-2 border-teal-200 ml-2">
                    {services.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => handleServiceClick(service.id)}
                        className="w-full text-left py-2 text-slate-600 hover:text-teal-600 transition-colors text-sm"
                      >
                        {service.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => scrollToSection('client-stories')}
                className="text-slate-700 hover:text-teal-600 font-medium text-left py-2 transition-colors"
              >
                Client Stories
              </button>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-teal-600 hover:bg-teal-700 text-white w-full mt-2"
              >
                Contact Us
              </Button>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
